import {
  Divider,
  Flex,
  Image,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Currency, DEFAULT_CURRENCY } from "@models/currency";
import { DEFAULT_LANGUAGE, translate } from "@models/language";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { formatPrice, usePaymentStore } from "../../payment";

const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

const OrderItemGroup = ({
  name,
  discount,
  quantity,
  subTotal,
  currency,
  imageUrl,
}: {
  name: string;
  discount: number;
  quantity: number;
  subTotal: number;
  currency: Currency;
  imageUrl: string;
}) => {
  return (
    <div className="text-sm dark:text-neutral-100 font-medium flex flex-row justify-between w-full items-center">
      <div className="flex-shrink flex md:flex md:items-center flex-wrap gap-4">
        {imageUrl && (
          <Image
            className="w-[2.5rem] h-[2.5rem] max-h-full rounded"
            objectFit="contain"
            src={imageUrl}
            alt={name}
          />
        )}
        <div className="mr-2">{`${quantity} x ${name}`}</div>

        {discount > 0 && (
          <Tag
            bgColor="primary.100"
            textColor="primary.400"
            px={3}
            py={1}
            borderRadius="sm"
          >
            {`${discount} % OFF`}
          </Tag>
        )}
      </div>

      <div className="flex-shrink-0">
        {subTotal > 0 ? formatPrice(subTotal, currency) : _t("Free")}
      </div>
    </div>
  );
};

const CheckoutOrderSummary = () => {
  const transaction = usePaymentStore((state) => state.transaction);
  const event = usePaymentStore((state) => state.event);
  const language = DEFAULT_LANGUAGE;

  const currency = useMemo(() => {
    return event?.currency || DEFAULT_CURRENCY;
  }, [event]);

  const itemGroupSummary = useMemo(() => {
    if (
      !event ||
      !transaction ||
      !transaction.items ||
      isEmpty(transaction.items)
    )
      return [];
    const item = transaction.items[0];
    const tier = event.tiers.find((tier) => tier.id == item.productId);
    if (!tier || !tier.paymentConfig) return [];
    return [
      {
        name: translate(tier.info, language).name || "",
        discount: tier.paymentConfig.currentPriceDiscountPercentage,
        imageUrl: tier.imageUrl,
        quantity: transaction.items.reduce(
          (acc, cur) => (acc += cur.quantity),
          0
        ),
        subTotal: transaction.originalAmount,
      },
    ];
    // TODO cross tier purchase
    // const itemsCountByProductId = countBy(transaction.items, "productId");
    // console.log(itemsCountByProductId);
    // return compact(
    //   Object.keys(itemsCountByProductId).map((productId) => {
    //     const tier = event.tiers.find((tier) => tier.id == productId);
    //     if (!tier || !tier.paymentConfig) return null;
    //     const quantity = itemsCountByProductId[productId];
    //     const subTotal = ceil(
    //       tier.paymentConfig.currentPrice.price * quantity,
    //       PAYMENT_AMOUNT_PRECISION
    //     );
    //     return {
    //       name: translate(tier.info, language).name || "",
    //       discount: tier.paymentConfig.currentPriceDiscountPercentage,
    //       imageUrl: tier.imageUrl,
    //       quantity,
    //       subTotal,
    //     };
    //   })
    // );
  }, [event, transaction]);

  const borderColor = useColorModeValue("", "neutral.10");

  if (!event || !transaction) return <></>;
  return (
    <div className="flex flex-col w-full justify-start items-start px-4 py-8 sm:p-8 border-t-2 dark:border-neutral-600 sm:border-none dark:text-white">
      <Text fontSize="xl" fontWeight={500}>
        {_t("Order Summary")}
      </Text>
      {transaction.items.length > 0 ? (
        <>
          <>
            <Flex mt={10} w="full" direction="column" gap={4}>
              {itemGroupSummary.map((group, index) => (
                <OrderItemGroup
                  key={index}
                  name={group.name}
                  discount={group.discount}
                  quantity={group.quantity}
                  subTotal={group.subTotal}
                  imageUrl={group.imageUrl}
                  currency={currency}
                />
              ))}

              {transaction.discount && transaction.discountAmount !== 0 && (
                <div className="text-sm text-primary-300 font-medium flex flex-row justify-between w-full">
                  <div className="flex-shrink">{_t("Discount")}</div>
                  <div className="flex-shrink-0">{`- ${formatPrice(
                    transaction.discountAmount,
                    currency
                  )}`}</div>
                </div>
              )}
            </Flex>
            <Divider borderColor={borderColor} my={8} />
            <Flex direction="column" gap={3} w="full">
              <div className="flex flex-row justify-between w-full text-2xl">
                <Text fontSize="md" fontWeight="medium">
                  {" "}
                  {_t("Sub-total")}{" "}
                </Text>
                <Text fontSize="md" fontWeight="medium">
                  {transaction.amount
                    ? formatPrice(transaction.amount, currency)
                    : _t("Free")}
                </Text>
              </div>

              {transaction.finalAmount > 0 &&
                transaction.transactionFee > 0 && (
                  <div className="text-sm flex flex-row justify-between w-full">
                    <div className="flex-shrink">{_t("Fees")}</div>
                    <div className="flex-shrink-0">{`+ ${formatPrice(
                      transaction.transactionFee,
                      currency
                    )}`}</div>
                  </div>
                )}
            </Flex>
          </>
          <div className="hidden sm:flex sm:w-full sm:flex-col">
            <Divider borderColor={borderColor} my={8} />
            <div className="flex flex-row justify-between w-full text-lg font-medium ">
              <Text> {_t("Total")} </Text>
              <Text>
                {transaction.finalAmount
                  ? `${formatPrice(transaction.finalAmount, currency)}`
                  : _t("Free")}
              </Text>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-start mt-6">
          <Text className="my-1 " fontSize="2md">
            {_t("No Order Yet")}
          </Text>
        </div>
      )}
    </div>
  );
};

export default CheckoutOrderSummary;
